import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, ChevronDown, ChevronUp, Clock, Trash2 } from "lucide-react";
import { canAnswerHere, keywordDate, keywordRowNote } from "@/lib/keywords";

export const KeywordRow = ({ kw, onRetry, busy, onDelete }) => {
  const [open, setOpen] = useState(false);
  const prefix = `keyword-${kw.id}`;
  return (
    <li className="border border-tw bg-tw-surface" data-testid={prefix}>
      <div className="flex items-stretch min-w-0">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid={`${prefix}-toggle`}
        className="tw-press flex-1 flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors duration-150 min-w-0">
        <span className="min-w-0 flex-1">
          <strong className="text-white break-words">{kw.word}</strong>
          <span className="block font-mono text-[10px] text-tw-muted mt-0.5 break-words" data-testid={`${prefix}-note`}>
            {keywordRowNote(kw)} · {kw.created_by_name || "Oud teamlid"}, {keywordDate(kw.created_at)}
          </span>
        </span>
        {open ? <ChevronUp size={14} className="text-tw-muted shrink-0" /> : <ChevronDown size={14} className="text-tw-muted shrink-0" />}
      </button>
      {onDelete && !kw.pending && (
        <button type="button" onClick={() => onDelete(kw)} disabled={busy} data-testid={`${prefix}-delete`} aria-label={`Trefwoord ${kw.word} verwijderen`}
          className="tw-press shrink-0 px-3 inline-flex items-center gap-1 border-l border-tw font-mono text-[10px] uppercase tracking-[0.15em] text-tw-muted hover:text-[#FF3B30] disabled:opacity-50 transition-colors duration-150">
          <Trash2 size={12} /> <span className="hidden sm:inline">Verwijderen</span>
        </button>
      )}
      </div>
      {kw.can_retry && (
        <button type="button" disabled={busy} onClick={() => onRetry?.(kw.id)} data-testid={`${prefix}-retry`}
          className="tw-press mx-4 mb-3 h-8 px-3 border border-tw-yellow text-tw-yellow font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-tw-yellow hover:text-black disabled:opacity-50">
          Opnieuw proberen
        </button>
      )}
      {open && (kw.questions.length ? <KeywordQuestions kw={kw} prefix={prefix} /> : (
        <p className="px-4 pb-3 text-xs text-tw-muted" data-testid={`${prefix}-none`}>
          {kw.pending ? "Vragen worden gemaakt…" : kw.all_deleted ? "Alle vragen zijn verwijderd."
            : kw.status === "done" ? "De vragen wachten nog op een beheerder." : "Er konden nog geen vragen gemaakt worden."}
        </p>
      ))}
    </li>
  );
};

export const KeywordQuestions = ({ kw, prefix }) => {
  const navigate = useNavigate();
  return (
    <ul className="mt-3 border-t border-tw divide-y divide-tw" data-testid={`${prefix}-questions`}>
      {kw.questions.map((q) => {
        const go = canAnswerHere(q);
        return (
          <li key={q.id}>
            <button type="button" disabled={!go} onClick={() => navigate(`/?vraag=${encodeURIComponent(q.id)}`)}
              data-testid={`${prefix}-question-${q.id}`} data-answerable={go ? "true" : "false"}
              className="tw-press w-full flex items-start gap-3 px-4 py-3 text-left text-sm text-white enabled:hover:text-tw-yellow disabled:cursor-default transition-colors duration-150">
              <span className="flex-1 min-w-0 break-words">{q.text}</span>
              <QuestionState q={q} />
            </button>
          </li>
        );
      })}
    </ul>
  );
};

const QuestionState = ({ q }) => {
  const base = "shrink-0 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em]";
  if (!q.active) return <span className={`${base} text-tw-muted`}><Clock size={10} /> wacht op goedkeuring</span>;
  if (q.answered) return <span className={`${base} text-tw-yellow`}><Check size={10} strokeWidth={3} /> beantwoord</span>;
  if (q.for_me === false) return <span className={`${base} text-tw-muted`} data-testid={`keyword-question-other-functie-${q.id}`}>voor een andere functie</span>;
  return <ArrowUpRight size={14} strokeWidth={2.5} className="shrink-0 text-tw-muted" />;
};
